import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import { GoalDtoIn } from '@/models/in/GoalDtoIn';
import { UpdateGoalTypeResultDtoIn } from '@/models/in/UpdateGoalTypeResultDtoIn';
import { UpdateGoalTypeDtoOut } from '@/models/out/UpdateGoalTypeDtoOut';
import { useGetAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import { FUND_EXPENSE_GOAL_NAMES, isFundraisingExpenseGoalName } from '@/constants/fundExpenseGoals';
import styles from './ChangeGoalTypeDialog.module.css';

interface ChangeGoalTypeDialogProps {
  open: boolean;
  onClose: () => void;
  request: ManagementSupportRequestDtoIn;
  onSuccess: () => void;
}

const ChangeGoalTypeDialog = ({ open, onClose, request, onSuccess }: ChangeGoalTypeDialogProps) => {
  const { t, i18n } = useTranslation();
  const currentGoalId = request.goal?.id ?? null;
  const currentGoalName = request.goal?.name ?? '';
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(currentGoalId);
  const [comment, setComment] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  const { data: fundExpenseGoals, isLoading: isLoadingGoals } = useGetAuthApi<GoalDtoIn[]>(
    open && request.fundId ? `funds/${request.fundId}/fund-expense-goals` : ''
  );

  const selectableGoals = useMemo(() => fundExpenseGoals ?? [], [fundExpenseGoals]);

  const selectedGoal = useMemo(
    () => selectableGoals.find((g) => g.id === selectedGoalId),
    [selectableGoals, selectedGoalId]
  );
  const targetIsFundraising = isFundraisingExpenseGoalName(selectedGoal?.name);
  const currentIsFundraising = isFundraisingExpenseGoalName(currentGoalName);

  // Fetch the fund's remaining fundraising eligibility as soon as the dialog opens so the
  // user can see it next to the "Fundraising" option before they even pick it. This value
  // reflects the fund's current state - if the request is already counted as fundraising
  // its amount is already inside the active sum (same view shown elsewhere in the app).
  const eligibilityUrl = `funds/${request.fundId}/fundraising-eligible-amount`;
  const { data: fundraisingEligibleAmount, isFetching: isFetchingEligibility } = useGetAuthApi<number>(
    open && request.fundId ? eligibilityUrl : ''
  );

  // Exceedance check only applies when moving the request INTO fundraising from a different
  // goal: the approvedAmount is not yet in the active sum, so we compare it to the current
  // eligibility. When the request is already fundraising the API value already accounts
  // for it; switching to a different fundraising goal isn't a meaningful "move in".
  const willExceedFundraisingLimit =
    targetIsFundraising &&
    selectedGoalId !== currentGoalId &&
    !currentIsFundraising &&
    fundraisingEligibleAmount != null &&
    request.approvedAmount > fundraisingEligibleAmount;

  const { mutate: updateGoalType, isPending: isSubmitting } = usePutAuthApi<
    UpdateGoalTypeResultDtoIn,
    object,
    UpdateGoalTypeDtoOut
  >('management/support-requests/goal-type');

  useEffect(() => {
    if (open) {
      setSelectedGoalId(currentGoalId);
      setComment('');
      setError(null);
      setResultMessage(null);
    } else {
      // Drop cached eligibility/goals so the next open shows fresh values instead of
      // briefly flashing stale numbers from a previous request/fund.
      queryClient.removeQueries({ queryKey: [eligibilityUrl] });
    }
  }, [open, currentGoalId, eligibilityUrl]);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString(i18n.language, { style: 'currency', currency: 'ILS' });

  const handleSubmit = () => {
    if (!selectedGoalId) {
      setError(t('changeGoalType.selectGoalRequired'));
      return;
    }
    if (selectedGoalId === currentGoalId) {
      onClose();
      return;
    }

    setError(null);
    setResultMessage(null);

    const body: UpdateGoalTypeDtoOut = {
      correlationId: request.multiRequestCorrelationId,
      counter: request.multiRequestCounter ?? null,
      newGoalTypeId: selectedGoalId,
      comment: comment.trim() || undefined,
    };

    updateGoalType(
      { body },
      {
        onSuccess: (result) => {
          // Always trigger the parent's refresh as soon as the update succeeds, even when
          // we keep the dialog open to show a confirmation message. Otherwise, if the user
          // closes the dialog via the X/backdrop instead of the "close" button, the
          // table/blade would never refresh.
          onSuccess();
          // Keep the dialog open after every successful update so the user always gets a
          // confirmation that the change went through (and, when applicable, the
          // status-transition info). The dialog only closes when the user explicitly
          // clicks "close".
          if (result.movedToExceptionsCommittee) {
            setResultMessage(t('changeGoalType.movedToExceptions'));
          } else if (result.movedBackFromExceptionsCommittee) {
            setResultMessage(t('changeGoalType.movedBackFromExceptions'));
          } else {
            setResultMessage(t('changeGoalType.updated'));
          }
        },
        onError: (err) => {
          setError(t('changeGoalType.errorUpdating') + ': ' + err.message);
        },
      }
    );
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
      title={t('changeGoalType.title')}
    >
      <div className={styles.content}>
        <div className={styles.infoPanel}>
          <div className={styles.infoPanelRow}>
            <span className={styles.infoPanelLabel}>{t('changeGoalType.currentGoal')}:</span>
            <span className={styles.infoPanelValue}>{t(currentGoalName)}</span>
          </div>
          <div className={styles.infoPanelRow}>
            <span className={styles.infoPanelLabel}>{t('requestedAmount')}:</span>
            <span className={styles.infoPanelValue}>{formatCurrency(request.requestedAmount)}</span>
          </div>
          <div className={styles.infoPanelRow}>
            <span className={styles.infoPanelLabel}>{t('approvedAmount')}:</span>
            <span className={styles.infoPanelValue}>{formatCurrency(request.approvedAmount)}</span>
          </div>
        </div>

        <div className={styles.section}>
          <label className={styles.label}>{t('changeGoalType.selectNewGoal')}</label>
          {isLoadingGoals ? (
            <p className={styles.hint}>{t('loading')}...</p>
          ) : (
            <div className={styles.radioGroup}>
              {selectableGoals.map((goal) => (
                <label key={goal.id} className={styles.radioOption}>
                  <input
                    type="radio"
                    name="goalType"
                    value={goal.id}
                    checked={selectedGoalId === goal.id}
                    onChange={() => setSelectedGoalId(goal.id)}
                    disabled={!!resultMessage}
                  />
                  <span>
                    {t(goal.name)}
                    {goal.name === FUND_EXPENSE_GOAL_NAMES.FUNDRAISING && (
                      <span className={styles.subtitleHint}>
                        {' '}
                        ({t('changeGoalType.fundraisingHint')}
                        {!isFetchingEligibility && fundraisingEligibleAmount != null && (
                          <>
                            {'; '}
                            {t('changeGoalType.remainingEligibility', {
                              amount: formatCurrency(fundraisingEligibleAmount),
                            })}
                          </>
                        )}
                        )
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className={styles.section}>
          <label htmlFor="goalType-comment" className={styles.label}>
            {t('note')}
          </label>
          <textarea
            id="goalType-comment"
            className={styles.textarea}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={!!resultMessage}
            rows={3}
          />
        </div>

        {targetIsFundraising && selectedGoalId !== currentGoalId && (
          <>
            {isFetchingEligibility || fundraisingEligibleAmount == null ? (
              <p className={styles.hint}>{t('changeGoalType.calculatingEligibility')}</p>
            ) : (
              <>
                <div className={styles.infoPanel}>
                  <div className={styles.infoPanelRow}>
                    <span className={styles.infoPanelLabel}>
                      {t('changeGoalType.currentFundraisingEligibility')}:
                    </span>
                    <span className={styles.infoPanelValue}>
                      {formatCurrency(fundraisingEligibleAmount)}
                    </span>
                  </div>
                </div>
                {willExceedFundraisingLimit ? (
                  <p className={styles.exceedsAlert}>
                    {t('changeGoalType.exceedsWarning', {
                      amount: formatCurrency(request.approvedAmount),
                      eligible: formatCurrency(fundraisingEligibleAmount),
                    })}
                  </p>
                ) : (
                  <p className={styles.warning}>{t('changeGoalType.fundraisingWarning')}</p>
                )}
              </>
            )}
          </>
        )}

        {error && <p className={styles.error}>{error}</p>}
        {resultMessage && <p className={styles.warning}>{resultMessage}</p>}

        <div className={styles.actions}>
          {resultMessage ? (
            <Button variant="primary" onClick={onClose}>
              {t('close')}
            </Button>
          ) : (
            <>
              <Button variant="white" onClick={onClose} disabled={isSubmitting}>
                {t('cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  isLoadingGoals ||
                  !selectedGoalId ||
                  selectedGoalId === currentGoalId
                }
              >
                {isSubmitting ? t('loading') : t('approve')}
              </Button>
            </>
          )}
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default ChangeGoalTypeDialog;
